import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { chosenFocalPoint, visibleItems } from "@/components/cms/SectionRenderer";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useMediaByKey, useSettings } from "@/content-context";
import type { MediaItem } from "@/data/media";
import { charityTrek } from "@/data/media";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader } from "@/page-data";

/**
 * Charity Trek.
 *
 * This page used to be written entirely into its component, while four sections
 * with nearly the same words sat in the CMS doing nothing: an editor could change
 * them and see no difference on the site. It renders those sections now.
 *
 * Mountain Routes handles the trekking half and operates its own website; HISCUS
 * handles the service half. The page names the partner from the organisation
 * profile, so a change of partner or address is an edit rather than a deploy.
 */

export const loader = pageLoader("charity-trek");

export const meta = () =>
  buildMetadata({
    title: "Charity Trek",
    description:
      "Trek in Nepal and take part in service: trail cleanup, school maintenance, classroom painting and library development, with Mountain Routes.",
    path: "/get-involved/charity-trek",
  });

function ExplainerSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const image = section.mediaKey ? byKey(section.mediaKey) : charityTrek[2];
  const steps = visibleItems(section);

  return (
    <Section>
      {image ? (
        <Reveal variants={imageReveal}>
          <MediaFigure
            item={image}
            ratio="21 / 9"
            sizes="(min-width: 1280px) 1180px, 100vw"
            objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
          />
        </Reveal>
      ) : null}

      {section.title || section.body?.length ? (
        <Reveal className="mt-14 max-w-3xl">
          {section.title ? <SectionHeading eyebrow={section.eyebrow} title={section.title} lead={section.lead} /> : null}
          <Blocks blocks={section.body} className={section.title ? "mt-6" : ""} />
        </Reveal>
      ) : null}

      {steps.length ? (
        <div className="mt-14 grid gap-12 lg:grid-cols-3 lg:gap-10">
          {steps.map((entry) => (
            <Reveal key={entry.title}>
              <div className="h-full rounded-[16px] border border-line bg-white p-6 md:p-7">
                <h3 className="font-serif text-[1.375rem] text-navy">{entry.title}</h3>
                {entry.text ? <p className="mt-3 text-muted">{entry.text}</p> : null}
              </div>
            </Reveal>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function ActivitiesSection({ section }: SectionProps) {
  const activities = visibleItems(section);
  if (!activities.length) return null;
  return (
    <Section tone="ivory" ariaLabelledBy="service-heading">
      <SectionHeading id="service-heading" eyebrow={section.eyebrow} title={section.title || "What participants actually do"} lead={section.lead} />
      <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {activities.map((activity) => (
          <Reveal as="li" key={activity.title}>
            <div className="h-full rounded-[16px] border border-line bg-white p-6">
              <h3 style={{ fontSize: "var(--step-h4)" }}>{activity.title}</h3>
              {activity.text ? <p className="mt-3 text-muted">{activity.text}</p> : null}
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function GallerySection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const photos = (section.mediaKeys ?? []).map((key) => byKey(key)).filter((item): item is MediaItem => Boolean(item));
  if (!photos.length) return null;
  return (
    <Section ariaLabelledBy="trek-gallery-heading">
      <SectionHeading id="trek-gallery-heading" eyebrow={section.eyebrow} title={section.title || "From programme days"} />
      <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((media) => (
          <Reveal as="li" key={media.id}>
            <MediaFigure item={media} ratio="4 / 3" sizes="(min-width: 1024px) 30vw, 100vw" />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function EnquirySection({ section }: SectionProps) {
  const { charityTrek: trek } = useSettings();
  return (
    <Section tone="mist" ariaLabelledBy="trek-enquiry-heading">
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
        <Reveal>
          <SectionHeading id="trek-enquiry-heading" eyebrow={section.eyebrow} title={section.title || "Dates, costs and what is included"} />
          <Blocks blocks={section.body} className="mt-6" />

          {trek.partnerName && trek.partnerUrl ? (
            <div className="mt-8 rounded-[14px] border border-line bg-white p-5">
              <p className="text-[0.9375rem] text-ink">
                Charity Trek is delivered with{" "}
                <a
                  href={trek.partnerUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold text-navy underline underline-offset-4"
                >
                  {trek.partnerName}
                </a>
                . Trekking logistics, guiding and safety are handled by the trekking partner.
              </p>
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <EnquiryForm heading={section.texts?.formHeading || "Enquire about a Charity Trek"} defaultInterest="charity-trek" />
        </Reveal>
      </div>
    </Section>
  );
}

export const charityTrekSections: SectionRenderers = {
  "charity-trek.explainer": ExplainerSection,
  "charity-trek.activities": ActivitiesSection,
  "charity-trek.gallery": GallerySection,
  "charity-trek.enquiry": EnquirySection,
};

export function CharityTrekPage() {
  return (
    <>
      <PageHeader
        eyebrow="Charity Trek"
        title="Trek. Serve. Sustain."
        lead="Run in partnership with Mountain Routes: a trekking route through Nepal, combined with service days that feed the schools, trails and community sites HISCUS already supports."
        trail={[
          { name: "Home", href: "/" },
          { name: "Get Involved", href: "/get-involved" },
          { name: "Charity Trek", href: "/get-involved/charity-trek" },
        ]}
      />
      <PageSections renderers={charityTrekSections} />
    </>
  );
}
